import { current } from '@reduxjs/toolkit';
import { createIntl, createIntlCache } from 'react-intl';
import { imperativeTranslateMessage } from '../../components/Translator';
import german from '../../resources/de.json';
import english from '../../resources/en.json';
import french from '../../resources/fr.json';
import italian from '../../resources/it.json';
import {
    PUREcalculateDrawingCoordinates,
    PUREcalculateOperatingDevices,
    PUREcalculateOverallLumenAndWatt,
    PUREcalculatePrices,
    PUREcalculateProfileDrawingCoordinates,
    PUREmapElementsToProducts,
} from './productsSlicePURE';

/**
 * Calculates Purelite Slim (PURE version)
 * @param state
 */
export function PUREdoCalculationPureliteSlim(state) {
    let userLength = state.userLengths.line1;
    let rulesetConstants = current(state).constants;
    let lineLengths = JSON.parse(current(state).lineLengths);

    // resetting prices, profileLengths & diffusorLengths
    state.productPriceEur = 0;
    state.productPriceChf = 0;
    state.profileLengths = null;
    state.diffusorLengths = null;
    var profileLengths = null;
    var diffusorLengths = null;

    let elements = [];
    let master = lineLengths.filter((item) => item.type === 'master');
    let slave = lineLengths.filter((item) => item.type === 'slave');
    master.sort((a, b) => b.length - a.length);
    slave.sort((a, b) => b.length - a.length);

    let sidebarValues;
    [elements, profileLengths, diffusorLengths, sidebarValues] = PUREcalculateLinePureliteSlim(
        state.userSelectedInstallation,
        state.language,
        state.userSelectedPureliteSlimLighting,
        userLength,
        rulesetConstants,
        master,
        slave,
        elements
    );

    let currentLineValues = JSON.parse(state.lineValues) || [];
    currentLineValues.push(JSON.stringify(sidebarValues));
    state.lineValues = JSON.stringify(currentLineValues);

    state.lineElements = JSON.stringify(elements);

    let [products, euro, chf] = PUREmapElementsToProducts(
        state.rulesetLightingSystemElements,
        state.rulesetName,
        state.userSelectedDiffusor,
        state.userSelectedPower,
        state.userSelectedColor,
        state.userProducts,
        0,
        0,
        elements
    );
    state.userProducts = products;

    let userProfiles = '';
    [userProfiles, euro, chf] = PUREmapProfilesToProductsPureliteSlim(
        state.profiles,
        JSON.parse(profileLengths),
        state.userSelectedInstallation,
        state.userSelectedPureliteSlimLighting,
        euro,
        chf
    );

    let userEQP = null;
    [userEQP, euro, chf] = PUREmapEQPToProductsPureliteSlim(
        JSON.parse(profileLengths),
        state.equipment,
        state.userSelectedInstallation,
        state.userSelectedPureliteSlimLighting,
        euro,
        chf
    );
    state.userEQP = JSON.stringify(userEQP);

    let userSystemEquipmentString = '';
    let userDiffusorsString = '';
    [userSystemEquipmentString, userDiffusorsString, euro, chf] = PUREmapSystemEquipmentToProductsPureliteSlim(
        euro,
        chf,
        state.systemEquipment,
        diffusorLengths,
        userProfiles,
        state.userSelectedDiffusor,
        state.userSelectedPureliteSlimLighting
    );
    state.productPriceEur = euro;
    state.productPriceChf = chf;

    state.userProfiles = userProfiles;
    state.userSystemEquipment = userSystemEquipmentString;
    state.userDiffusors = userDiffusorsString;

    let coordinates = [];
    state.isSimplifiedDrawing = true;
    if (current(state).isSimplifiedDrawing) {
        coordinates = PUREcalculateProfileDrawingCoordinates(
            state.userSelectedForm,
            state.userSelectedInstallation,
            state.rulesetName,
            state.selectedVisibleLayer,
            userProfiles,
            profileLengths,
            state.isSimplifiedDrawing,
            state.drawingSimpleCoordinates,
            elements,
            false
        );
        state.profileDrawingSimpleCoordinates = coordinates;
    }

    state.isSimplifiedDrawing = false;
    coordinates = PUREcalculateProfileDrawingCoordinates(
        state.userSelectedForm,
        state.userSelectedInstallation,
        state.rulesetName,
        state.selectedVisibleLayer,
        userProfiles,
        profileLengths,
        state.isSimplifiedDrawing,
        state.drawingSimpleCoordinates,
        elements,
        false
    );
    state.profileDrawingCoordinates = coordinates;

    const [lumen, watt] = PUREcalculateOverallLumenAndWatt(state.userProducts, state.productCombinations, state.userSelectedDiffusor, state.userSelectedPower);
    state.overallLumen = lumen;
    state.overallWatt = watt;
    state.operatingDevices = PUREcalculateOperatingDevices(elements);

    state.isSimplifiedDrawing = true;
    if (current(state).isSimplifiedDrawing) {
        coordinates = PUREcalculateDrawingCoordinates(
            state.userSelectedForm,
            state.rulesetName,
            state.selectedVisibleLayer,
            state.userProducts,
            state.isSimplifiedDrawing,
            state.drawingSimpleCoordinates,
            elements,
            false
        );
        state.drawingSimpleCoordinates = coordinates;
    }
    state.isSimplifiedDrawing = false;
    coordinates = PUREcalculateDrawingCoordinates(
        state.userSelectedForm,
        state.rulesetName,
        state.selectedVisibleLayer,
        state.userProducts,
        state.isSimplifiedDrawing,
        state.drawingSimpleCoordinates,
        elements,
        false
    );
    state.drawingCoordinates = coordinates;
}

/**
 * Calculates a single line for Purelite Slim
 *
 * @param {string} userSelectedInstallation
 * @param {string} language
 * @param {string} lightDirection
 * @param {string} userLength
 * @param {Object} rulesetConstants
 * @param {Array} master
 * @param {Array} slave
 * @param {Array} elements
 * @param {number|null} [lightLength=null]
 * @returns {[Array, string, string, Array]} [elements, profileLengths, diffusorLengths, sidebarValues]
 */
export function PUREcalculateLinePureliteSlim(
    userSelectedInstallation,
    language,
    lightDirection,
    userLength,
    rulesetConstants,
    master,
    slave,
    elements,
    lightLength = null
) {
    let slimLengths = PUREcalculateLengthsPureliteSlim(lightDirection, userLength, rulesetConstants, lightLength);
    lightLength = slimLengths.lightLength;

    let [sidebarValues, profileLengths, diffusorLengths] = PUREdisplayValuesPureliteSlim(
        userSelectedInstallation,
        language,
        'L1',
        slimLengths.userLength,
        slimLengths.recessLength,
        slimLengths.profileLength,
        0,
        slimLengths.diffusorLength,
        lightLength,
        slimLengths.totalLength
    );

    elements = PUREcalculateElementsPureliteSlim(elements, master, slave, lightLength);

    return [elements, profileLengths, diffusorLengths, sidebarValues];
}

/**
 * @typedef {Object} SlimLengthStruct
 * @property {number} diffusorLength
 * @property {number} lightLength
 * @property {number} profileLength
 * @property {number} recessLength
 * @property {number} totalLength
 * @property {number} userLength
 */

/**
 * Calculates all needed lengths for Purelite Slim
 *
 * @param {string} lightDirection
 * @param {string|number} userLength
 * @param {Object} rulesetConstants
 * @param {number|null} [lightLength=null]
 * @returns {SlimLengthStruct}
 */
export function PUREcalculateLengthsPureliteSlim(lightDirection, userLength, rulesetConstants, lightLength = null) {
    userLength = parseFloat(userLength);

    let profileLength = userLength - 2 * rulesetConstants.endwallthickness;
    let recessLength = Math.ceil(profileLength + rulesetConstants.cutoutcoefficient + rulesetConstants.expansion * profileLength);
    let diffusorLength = Math.ceil(userLength - userLength * rulesetConstants.expansion);

    if (lightLength === null) {
        if (profileLength - (Math.floor((profileLength / 100) * 4) / 4) * 100 < 4) {
            lightLength = (Math.floor(((profileLength - 4) / 100) * 4) / 4) * 100;
        } else {
            lightLength = (Math.floor((profileLength / 100) * 4) / 4) * 100;
        }
    }

    let totalLength = profileLength + rulesetConstants.endwallthickness * 2;

    if (lightDirection === 'direktstrahlend') {
        const end = 3;
        profileLength = userLength - 2 * end;

        // Modifier defined in Purelite Slim_Konfigurator_V1.6
        let diffusor_modifier = 0;
        if (userLength / 1000 > 0 && userLength / 1000 < 5) {
            diffusor_modifier = 5;
        } else if (userLength / 1000 >= 5 && userLength / 1000 < 10) {
            diffusor_modifier = 7;
        } else if (userLength / 1000 >= 10 && userLength / 1000 < 15) {
            diffusor_modifier = 10;
        } else if (userLength / 1000 >= 15 && userLength / 1000 < 20) {
            diffusor_modifier = 12;
        } else if (userLength / 1000 >= 15 && userLength / 1000 < 40) {
            diffusor_modifier = 15;
        }

        lightLength =
            profileLength - (Math.floor((profileLength / 100) * 4) / 4) * 100 < 4
                ? (Math.floor(((profileLength - 4) / 100) * 4) / 4) * 100
                : (Math.floor((profileLength / 100) * 4) / 4) * 100;

        recessLength = Math.ceil(userLength + rulesetConstants.cutoutcoefficient + rulesetConstants.expansion * profileLength);
        diffusorLength = userLength - 2 * diffusor_modifier;
        totalLength = userLength;
    }

    return {
        diffusorLength,
        lightLength,
        profileLength,
        recessLength,
        totalLength,
        userLength,
    };
}

/**
 * Calculates elements for Purelite Slim
 *
 * @param {Array} elements
 * @param {Array} master
 * @param {Array} slave
 * @param {number} calcVal
 * @returns {Array}
 */
export function PUREcalculateElementsPureliteSlim(elements, master, slave, calcVal) {
    calcVal = PUREcalculateMasterElementsPureliteSlim(elements, master, calcVal);
    PUREcalculateSlaveElementsPureliteSlim(elements, calcVal, slave);
    return elements;
}

/**
 * Calculates master elements for Purelite Slim
 *
 * @param {Array} elements
 * @param {Array} master
 * @param {number} calcVal
 * @returns {number} remaining calcVal
 */
export function PUREcalculateMasterElementsPureliteSlim(elements, master, calcVal) {
    let breakWhile = false;
    while (!breakWhile) {
        for (let i = 0; i < master.length; i++) {
            if (calcVal - master[i].length === 0 || (calcVal - master[i].length >= 75 && (calcVal - master[i].length > 725 || master[i].length < 2400))) {
                elements.push(master[i]);
                calcVal -= master[i].length;
                break;
            } else {
                if (i === master.length - 1) {
                    breakWhile = true;
                    break;
                }
            }
        }
    }
    return calcVal;
}

/**
 * Calculates slave elements for Purelite Slim
 *
 * @param {Array} elements
 * @param {number} calcVal
 * @param {Array} slave
 */
export function PUREcalculateSlaveElementsPureliteSlim(elements, calcVal, slave) {
    for (let i = 0; i < slave.length; i++) {
        if (calcVal - slave[i].length === 0) {
            elements.push(slave[i]);
            calcVal -= slave[i].length;
            break;
        }
    }
}

/**
 * Produces display values for sidebar and computes profile/diffusor length arrays
 *
 * @param {string} userSelectedInstallation
 * @param {string} language
 * @param {string} lineName
 * @param {number} userLength
 * @param {number} recess
 * @param {number} profileLength
 * @param {number} luminousSurface
 * @param {number} diffusorLength
 * @param {number} lightLength
 * @param {number} totalLength
 * @returns {[Array, string, string]} [lineValues, profileLengths, diffusorLengths]
 */
function PUREdisplayValuesPureliteSlim(
    userSelectedInstallation,
    language,
    lineName,
    userLength,
    recess,
    profileLength,
    luminousSurface,
    diffusorLength,
    lightLength,
    totalLength
) {
    let lineCount = 1;
    let lineValues = [];

    const cache = createIntlCache();
    const messages = { de: german, en: english, it: italian, fr: french };
    const intl = createIntl({ locale: language, messages: messages[language] });

    lineValues.push({ id: lineCount++, label: 'Name', value: lineName, suffix: '' });
    lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.total.title'), value: totalLength, suffix: 'mm' });
    lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.diffusor.title'), value: diffusorLength, suffix: 'mm' });
    lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.user.title'), value: userLength, suffix: 'mm' });

    if (userSelectedInstallation === 'recessed') {
        lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.recess'), value: recess, suffix: 'mm' });
    }

    lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.profile.title'), value: profileLength, suffix: 'mm' });
    lineValues.push({ id: lineCount++, label: imperativeTranslateMessage(intl, 'canvas.right.length.light.title'), value: lightLength, suffix: 'mm' });

    let profileLengthsLine = [{ line: lineName, profile: profileLength }];
    let profileLengths = JSON.stringify(profileLengthsLine);

    let diffusorLengthsLine = [{ line: lineName, length: diffusorLength }];
    let diffusorLengths = JSON.stringify(diffusorLengthsLine);

    return [lineValues, profileLengths, diffusorLengths];
}

/**
 * Maps equipment to products for Purelite Slim
 *
 * @param {Array} profileLengths
 * @param {string} equipmentState
 * @param {string} userSelectedInstallation
 * @param {string} userSelectedPureliteSlimLighting
 * @param {number} productPriceEur
 * @param {number} productPriceChf
 * @returns {[Array, number, number]}
 */
export function PUREmapEQPToProductsPureliteSlim(
    profileLengths,
    equipmentState,
    userSelectedInstallation,
    userSelectedPureliteSlimLighting,
    productPriceEur,
    productPriceChf
) {
    const equipment = JSON.parse(equipmentState);
    const installation = userSelectedInstallation;
    const lighting = userSelectedPureliteSlimLighting;
    let totalProfileLength = 0;

    profileLengths.forEach((profile) => (totalProfileLength += profile.profile));

    let userEquipment = [];

    // Stirnseiten-Set
    if (lighting === 'direktstrahlend') {
        if (installation === 'recessed') {
            userEquipment.push(equipment.filter((item) => item.article_nr === '2004.4078')[0]);
        } else if (installation === 'ceiling') {
            userEquipment.push(equipment.filter((item) => item.article_nr === '2004.4077')[0]);
        }
    } else {
        userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT S EQP FRONTEND-KIT')[0]);
    }

    // Einspeise-Set
    userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT S EQP INFD-SET')[0]);

    // Befestigungs-Set Purelite Slim D, FASTEN-KIT alle 1.5m
    if (installation === 'recessed') {
        for (let i = 0; i < Math.floor(totalProfileLength / 1500); i++) {
            if (installation === 'recessed') {
                userEquipment.push(equipment.filter((item) => item.article_nr === '2004.4082')[0]); // PURLT S EQP FASTEN-KIT CT
            } else if (installation === 'ceiling' || installation === 'pendulum') {
                userEquipment.push(equipment.filter((item) => item.article_nr === '2004.4083')[0]); // PURLT S EQP FASTEN-KIT TL
            }
        }
    }

    if (installation === 'ceiling') {
        // STANDARD: Schlitzscheiben-Set
        if (true) {
            let neededSuspensions = Math.ceil(totalProfileLength / 1500);
            while (neededSuspensions > 0) {
                if (neededSuspensions >= 5) {
                    userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT EQP WSH-SLO-KIT 5PCS')[0]);
                    neededSuspensions -= 5;
                } else {
                    userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT EQP WSH-SLO-KIT 2PCS')[0]);
                    neededSuspensions -= 2;
                }
            }
        }
        // OPTION 1: Befestigungsfeder not accessible yet
        else if (false) {
            for (let i = 0; i < Math.floor(totalProfileLength / 1500); i++) {
                userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT S EQP MNTSPRING')[0]);
            }
        }
    }

    // Aufhängungs-Set (1 every 1.5m)
    if (installation === 'pendulum') {
        let neededSuspensions = Math.ceil(totalProfileLength / 1500);
        for (let i = 0; i < neededSuspensions; i++) {
            if (i === 0) {
                userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT S EQP SUSP-KIT-1500-WITHCBL/5P')[0]);
            } else {
                userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT S EQP SUSP-KIT-1500')[0]);
                i++; // Increment iterator because this article is a 2-Set
            }
        }

        // Baldachin-Set
        for (let i = 0; i < 1; i++) {
            userEquipment.push(equipment.filter((item) => item.article_code === 'UNVERSL EQP CANOPY-KIT-132x56x25-RAL9016')[0]);
        }
    }

    // Zuschneidehilfe, nur bei RUN
    if (lighting === 'direktstrahlend') {
        userEquipment.push(equipment.filter((item) => item.article_code === 'PURLTD S EQP CUTT AID')[0]);
    }

    // Always include disassembly tool Channel S & Purelite (Slim)
    userEquipment.push(equipment.filter((item) => item.article_code === 'PURLT CHAN-S EQP TOOL YE')[0]);

    const [euro, chf] = PUREcalculatePrices(productPriceEur, productPriceChf, userEquipment);
    return [userEquipment, euro, chf];
}

/**
 * Maps system components to products for Purelite Slim
 *
 * @param {number} priceEuro
 * @param {number} priceChf
 * @param {string} systemEquipmentState
 * @param {string} diffusorLengthsState
 * @param {string} userProfiles
 * @param {string} userSelectedDiffusor
 * @param {string} userSelectedPureliteSlimLighting
 * @returns {[string, string, number, number]}
 */
export function PUREmapSystemEquipmentToProductsPureliteSlim(
    priceEuro,
    priceChf,
    systemEquipmentState,
    diffusorLengthsState,
    userProfiles,
    userSelectedDiffusor,
    userSelectedPureliteSlimLighting
) {
    let systemEquipment = JSON.parse(systemEquipmentState);
    let diffusorLengths = JSON.parse(diffusorLengthsState);
    let profiles = JSON.parse(userProfiles);

    // Convert object into array to use filter method
    if (typeof systemEquipment === 'object') {
        let systemEquipmentIndexes = [];
        Object.keys(systemEquipment).forEach((key) => {
            systemEquipmentIndexes.push(key);
        });
        let temp = [];
        for (let i = 0; i < systemEquipmentIndexes.length; i++) {
            temp.push(systemEquipment[systemEquipmentIndexes[i]]);
        }
        systemEquipment = temp;
    }

    let userSystemEquipment = [];
    let diffusor = userSelectedDiffusor.toLowerCase();
    let userDiffusors = [];

    let diffusorRUN = systemEquipment.filter((item) => item.diffusor === 'RUN');
    let diffusorRUNPlus = systemEquipment.filter((item) => item.diffusor === 'RUN+');
    let diffusorOpal = systemEquipment.filter((item) => item.article_code.includes('PURLT S SYS EQP DIF CW'));

    diffusorRUN = diffusorRUN.sort((a, b) => a.length - b.length);
    diffusorRUNPlus = diffusorRUNPlus.sort((a, b) => a.length - b.length);
    diffusorOpal = diffusorOpal.sort((a, b) => a.length - b.length);

    let diffusorsForCalc = diffusor === 'run' ? diffusorRUN : diffusor === 'run+' ? diffusorRUNPlus : diffusorOpal;
    /** For room lighting: Johannes wants to mostly use diffusors with one edge cover -> 1EF and none with both like -> 2EF
    Because 2EF are only useful if they fit the installation perfectly. Therefore we disregard them.
    We should at least send 2x 1EF even for the smallest configuration and add 0EF if they are needed.
     */
    let diffusors1EF = diffusorsForCalc.filter((item) => item.article_code.includes('1EF'));
    let diffusorsOEF = diffusorsForCalc.filter((item) => item.article_code.includes('OEF'));
    let diffusors2EF = (diffusor === 'run+' ? diffusorRUNPlus : diffusorOpal).filter((item) => item.article_code.includes('2EF'));

    let neededDifs = [];
    let diffusorsForCalcOnlyLenghts = [];
    let diffusorLength2EF = [];

    diffusorsForCalc.forEach((item) => diffusorsForCalcOnlyLenghts.push(item.length));
    diffusors2EF.forEach((item) => diffusorLength2EF.push(item.length));

    diffusors2EF.forEach((item) => {
        for (let i = 0; i < diffusorLengths.length; i++) {
            if (item.length === diffusorLengths[i].length) {
                neededDifs.push(item);
                diffusorLengths[i].length = 0;
            }
        }
    });

    // Different calculation based on selected lighting
    // --> Only room lighting uses 1EF and OEF so differentiate between it and direct lighting
    if (userSelectedPureliteSlimLighting === 'direktstrahlend') {
        for (let i = 0; i < diffusorLengths.length; i++) {
            let neededLength = diffusorLengths[i].length;

            while (neededLength > 0) {
                if (neededLength > diffusorsForCalcOnlyLenghts[diffusorsForCalcOnlyLenghts.length - 1]) {
                    neededDifs.push(diffusorsForCalc[diffusorsForCalcOnlyLenghts.length - 1]);
                    neededLength -= diffusorsForCalcOnlyLenghts[diffusorsForCalcOnlyLenghts.length - 1];
                } else {
                    for (let y = 0; y < diffusorsForCalc.length; y++) {
                        if (diffusorsForCalc[y].length >= neededLength) {
                            neededDifs.push(diffusorsForCalc[y]);
                            neededLength -= diffusorsForCalc[y].length;
                            break;
                        }
                    }
                }
            }
        }
    } else {
        for (let i = 0; i < diffusorLengths.length; i++) {
            if (diffusorLengths[i].length === 0) {
                continue;
            }
            let neededLength = diffusorLengths[i].length;
            let fittingIndex = diffusors1EF.length - 1;

            // First select the end pieces going from biggest to smallest, default is biggest
            for (let j = diffusors1EF.length - 1; j >= 0; j--) {
                if (2 * diffusors1EF[j].length >= neededLength) {
                    fittingIndex = j;
                }
            }

            // Add the 1EF for beginning and end
            neededDifs.push(diffusors1EF[fittingIndex]);
            neededDifs.push(diffusors1EF[fittingIndex]);
            neededLength -= diffusors1EF[fittingIndex].length * 2;

            // Fill the middle part of an installation bigger than the two biggest 1EF together
            while (neededLength > 0) {
                if (neededLength > diffusorsOEF[diffusorsOEF.length - 1].length) {
                    neededDifs.push(diffusorsOEF[diffusorsOEF.length - 1]);
                    neededLength -= diffusorsOEF[diffusorsOEF.length - 1].length;
                } else {
                    for (let y = 0; y < diffusorsOEF.length; y++) {
                        if (diffusorsOEF[y].length >= neededLength) {
                            neededDifs.push(diffusorsOEF[y]);
                            neededLength -= diffusorsOEF[y].length;
                            break;
                        }
                    }
                }
            }
        }
    }

    neededDifs.forEach((dif) => userDiffusors.push(dif));

    // Profilverbinder-Set
    for (let i = 0; i < profiles.length - 1; i++) {
        userSystemEquipment.push(systemEquipment.filter((item) => item.article_code === 'PURLT S SYS EQP LIN-CON-KIT')[0]);
    }

    let [euro, chf] = PUREcalculatePrices(priceEuro, priceChf, userSystemEquipment);
    [euro, chf] = PUREcalculatePrices(euro, chf, userDiffusors);
    return [JSON.stringify(userSystemEquipment), JSON.stringify(userDiffusors), euro, chf];
}

/**
 * Maps profiles to products for Purelite Slim
 *
 * @param {string} profilesState
 * @param {Array} profileLengthsState
 * @param {string} userSelectedInstallation
 * @param {string} userSelectedPureliteSlimLighting
 * @param {number} productPriceEur
 * @param {number} productPriceChf
 * @returns {[string, number, number]}
 */
export function PUREmapProfilesToProductsPureliteSlim(
    profilesState,
    profileLengthsState,
    userSelectedInstallation,
    userSelectedPureliteSlimLighting,
    productPriceEur,
    productPriceChf
) {
    let profiles = JSON.parse(profilesState);
    let profileLengths = profileLengthsState;
    let installation = userSelectedInstallation;
    let lighting = userSelectedPureliteSlimLighting;

    if (typeof profiles === 'object') {
        let profileIndexes = [];
        Object.keys(profiles).forEach((key) => {
            profileIndexes.push(key);
        });
        let temp = [];
        for (let i = 0; i < profileIndexes.length; i++) {
            temp.push(profiles[profileIndexes[i]]);
        }
        profiles = temp;
    }

    // Johannes wants recessed installation with edge cover (CT) and pendulum as well as ceiling without any (TL)
    switch (installation) {
        case 'ceiling':
        case 'pendulum':
            installation = 'TL';
            break;
        case 'recessed':
            installation = 'CT';
            break;
        default:
            console.log('An unexpected installation type was selected. TYPE:' + installation);
            break;
    }

    profiles.sort((a, b) => b.length - a.length);

    let ctProfiles = profiles.filter((item) => item.article_code.includes('CT') && !item.article_code.includes('.V'));
    let tlProfilesPureliteSlim = profiles.filter(
        (item) => item.article_code.includes('TL') && item.product_line === 'Purelite Slim' && !item.article_code.includes('.V')
    );
    let tlProfilesPureliteSlimD = profiles.filter(
        (item) => item.article_code.includes('TL') && item.product_line === 'Purelite Slim D' && !item.article_code.includes('.V')
    );

    let ctProfileLengths = [];
    let tlProfilesPureliteSlimLengths = [];
    let tlProfilesPureliteSlimDLengths = [];

    ctProfiles.map((item) => ctProfileLengths.push(item.length));
    tlProfilesPureliteSlim.map((item) => tlProfilesPureliteSlimLengths.push(item.length));
    tlProfilesPureliteSlimD.map((item) => tlProfilesPureliteSlimDLengths.push(item.length));

    let ctZuschnittProfiles = profiles.filter((item) => item.article_code.includes('CT') && item.article_code.includes('.V'));
    let tlSlimZuschnittProfiles = profiles.filter(
        (item) => item.article_code.includes('TL') && item.product_line === 'Purelite Slim' && item.article_code.includes('.V')
    );
    let tlSlimDZuschnittProfiles = profiles.filter(
        (item) => item.article_code.includes('TL') && item.product_line === 'Purelite Slim D' && item.article_code.includes('.V')
    );

    let profileProducts = [];

    profileLengths.forEach(function (profile) {
        let rest = profile.profile;
        let redoCalculation500Smaller = false;
        let add500 = false;

        let neededProfiles = installation === 'CT' ? ctProfiles : lighting === 'direktstrahlend' ? tlProfilesPureliteSlimD : tlProfilesPureliteSlim;
        let neededZuschnitt = installation === 'CT' ? ctZuschnittProfiles : lighting === 'direktstrahlend' ? tlSlimDZuschnittProfiles : tlSlimZuschnittProfiles;
        let neededProfileLengths =
            installation === 'CT' ? ctProfileLengths : lighting === 'direktstrahlend' ? tlProfilesPureliteSlimDLengths : tlProfilesPureliteSlimLengths;

        while (rest > 0) {
            for (let i = 0; i < neededProfileLengths.length; i++) {
                let currentValue = neededProfileLengths[i];
                if (rest - currentValue >= 0 && rest - currentValue > 500) {
                    profileProducts.push({ product: neededProfiles.filter((item) => item.length === currentValue)[0], cut: null, line: profile.line });
                    rest -= currentValue;
                    break;
                } else if (rest - currentValue >= 0 && rest - currentValue < 500) {
                    profileProducts.push({ product: neededProfiles.filter((item) => item.length === currentValue)[0], cut: null, line: profile.line });
                    rest -= currentValue;
                    break;
                }
            }
            if (rest < neededProfileLengths[neededProfileLengths.length - 1] && rest > 0) {
                if (rest > 2000) {
                    profileProducts.push({
                        product: neededZuschnitt.filter((item) => item.matchcode.includes('V2001-4803'))[0],
                        cut: add500 ? rest + 500 : rest,
                        line: profile.line,
                    });
                } else {
                    if (rest < 500) {
                        redoCalculation500Smaller = true;
                    } else {
                        profileProducts.push({
                            product: neededZuschnitt.filter((item) => item.matchcode.includes('V0-2000'))[0],
                            cut: add500 ? rest + 500 : rest,
                            line: profile.line,
                        });
                    }
                }
                if (redoCalculation500Smaller) {
                    rest = profile.profile - 500;
                    profileProducts = [];
                    redoCalculation500Smaller = false;
                    add500 = true;
                } else {
                    rest = 0;
                }
            }
        }
    });

    const [euro, chf] = PUREcalculatePrices(productPriceEur, productPriceChf, profileProducts);
    return [JSON.stringify(profileProducts), euro, chf];
}
